// Shared engine for every scene: constants, loop-safe deterministic helpers,
// pixel canvas utilities, dithered halos, a 3x5 pixel font, the billboard pitch and water helpers.
// Scenes must only import from this file.

/* ================= Constants ================= */
export const W = 480, H = 270;                  // internal resolution (16:9)
export const SCALE = 4, OUT_W = W * SCALE, OUT_H = H * SCALE;   // 1920x1080 export
export const LOOP = 240;                        // every scene loops perfectly every 240 s
export const FPS = 60;
export const TAU = Math.PI * 2;

/* ================= Deterministic, loop-safe helpers =================
   Every animation must be a function of t in [0, LOOP):
   - waves: integer cycles per loop            -> wave(t, k, ph)
   - random steps: integer steps per loop      -> step(t, perLoop), hash(...)
   - particles: integer cycles per loop        -> cycle(t, n, ph)                */
export const frac = v => v - Math.floor(v);
export const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
export const lerp = (a, b, t) => a + (b - a) * t;
export const smooth = (a, b, v) => { const x = clamp((v - a) / (b - a), 0, 1); return x * x * (3 - 2 * x); };
/** sin wave with k (integer!) cycles per loop */
export const wave = (t, k, ph = 0) => Math.sin(TAU * (k * t / LOOP + ph));
/** index of the current step when the loop is split into perLoop (integer) steps */
export const step = (t, perLoop) => Math.floor(t * perLoop / LOOP) % perLoop;
/** smooth 0→1→0 envelope over [a, b] with fade length f (all in loop seconds) */
export const env = (t, a, b, f) => smooth(a, a + f, t) * (1 - smooth(b - f, b, t));
export const inEvent = (t, list) => list.some(([a, b]) => t >= a && t < b);
/** particle clock: n (integer) cycles per loop. Returns the cycle index (0..n-1) and phase u in [0,1). */
export function cycle(t, n, ph = 0) {
  const q = t * n / LOOP + ph, f = Math.floor(q);
  return { cyc: ((f % n) + n) % n, u: q - f };
}
/** integer pixel offset for something scrolling k (integer) times across `width` per loop */
export const scroll = (t, k, width) => Math.floor(frac(k * t / LOOP) * width);

/** stateless integer hash -> [0, 1) */
export function hash(a, b = 0, c = 0) {
  let h = 2166136261 ^ Math.imul(a | 0, 374761393);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= Math.imul(b | 0, 668265263); h = Math.imul(h ^ (h >>> 15), 2246822519);
  h ^= Math.imul(c | 0, 3266489917); h = Math.imul(h ^ (h >>> 13), 3266489917);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
/** seeded PRNG (mulberry32) for build-time layout; never call it inside render(t) */
export function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0; let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map(v => (v + 0.5) / 16);
/** 4x4 ordered-dither threshold in (0, 1) */
export const bayer = (x, y) => BAYER[((y & 3) << 2) | (x & 3)];

/* ================= Colour ================= */
export const hex = h => { const n = parseInt(h.slice(1), 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; };
/** css rgb() string between two #rrggbb colours */
export function mix(a, b, t) {
  const A = hex(a), B = hex(b);
  return `rgb(${Math.round(lerp(A[0], B[0], t))},${Math.round(lerp(A[1], B[1], t))},${Math.round(lerp(A[2], B[2], t))})`;
}
export function rgba(h, a) { const [r, g, b] = hex(h); return `rgba(${r},${g},${b},${a})`; }

/* ================= Canvas ================= */
// Chrome empties every <canvas> of the page when its GPU process restarts (a driver reset, a GPU switch, memory
// pressure: `contextlost`), the ones a scene drew ahead and never touches again included, and a scene can't draw them
// twice. An OffscreenCanvas kept for reading keeps its picture through it (measured in Chrome 154 on macOS, issue #81),
// so there everything drawn ahead goes on those: the same pixels, as fast. Phones and other browsers, where neither was
// measured, keep the <canvas> (site/js/app.js builds the scenes again there, as far as that goes: contextRestored).
const UA = typeof navigator === 'object' ? navigator.userAgentData : null;
const KEPT = typeof OffscreenCanvas === 'function' && !!UA && !UA.mobile && !!UA.brands?.some(b => b.brand === 'Chromium');
/** CPU-backed canvas + context (willReadFrequently) with smoothing off; `element`: a <canvas> to put on the page */
export function makeCanvas(w, h, element = false) {
  const c = KEPT && !element ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const x = c.getContext('2d', { willReadFrequently: true });
  x.imageSmoothingEnabled = false;
  return [c, x];
}
export const px = (c, x, y, col) => { c.fillStyle = col; c.fillRect(x, y, 1, 1); };
export const rect = (c, x, y, w, h, col) => { c.fillStyle = col; c.fillRect(x, y, w, h); };

/**
 * Build a canvas by ordered-dithering between palette colours.
 * valueFn(x, y) returns 0..1 (0 = palette[0], 1 = last colour); omitted pixels stay transparent if it returns < 0.
 */
export function ditherFill(w, h, palette, valueFn) {
  const pal = palette.map(hex);
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const v = valueFn(i, y);
    if (v < 0) continue;
    const f = clamp(v, 0, 1) * (pal.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = pal[Math.min(k, pal.length - 1)], o = (y * w + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Halos (pre-rendered, dithered, drawn additively) ================= */
export function makeHalo(r, col, maxA, pow = 2, levels = 6) {
  const rgb = hex(col), s = r * 2 + 1;
  const [c, x] = makeCanvas(s, s);
  const img = x.createImageData(s, s), d = img.data;
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dd = Math.hypot(i - r, y - r) / r;
    if (dd >= 1) continue;
    const q = Math.floor(Math.pow(1 - dd, pow) * levels + bayer(i, y)) / levels;
    if (q <= 0) continue;
    const o = (y * s + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2];
    d[o + 3] = Math.round(Math.min(1, q) * maxA * 255);
  }
  x.putImageData(img, 0, 0);
  return { c, r };
}
/** draw a halo with 'lighter' compositing at intensity a (0..1) */
export function drawHalo(ctx, hl, x, y, a = 1) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(hl.c, Math.round(x) - hl.r, Math.round(y) - hl.r);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
}

/* ================= Pixel font ================= */
/** draw a glyph given as rows of '0'/'1' strings */
export function glyph(c, g, x, y, col) {
  c.fillStyle = col;
  g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') c.fillRect(x + i, y + j, 1, 1); });
}
/** 3x5 uppercase font for signs (A-Z, 0-9, a few symbols, the accented letters the city ads spell). Accents sit
    inside the 5-row cell so no layout grows; Ç's cedilla is a 6th row below the baseline. */
export const FONT3 = {
  A: ['010', '101', '111', '101', '101'], B: ['110', '101', '110', '101', '110'], C: ['011', '100', '100', '100', '011'],
  D: ['110', '101', '101', '101', '110'], E: ['111', '100', '110', '100', '111'], F: ['111', '100', '110', '100', '100'],
  G: ['011', '100', '101', '101', '011'], H: ['101', '101', '111', '101', '101'], I: ['111', '010', '010', '010', '111'],
  J: ['001', '001', '001', '101', '010'], K: ['101', '101', '110', '101', '101'], L: ['100', '100', '100', '100', '111'],
  M: ['101', '111', '111', '101', '101'], N: ['110', '101', '101', '101', '101'], O: ['010', '101', '101', '101', '010'],
  P: ['110', '101', '110', '100', '100'], Q: ['010', '101', '101', '110', '011'], R: ['110', '101', '110', '101', '101'],
  S: ['011', '100', '010', '001', '110'], T: ['111', '010', '010', '010', '010'], U: ['101', '101', '101', '101', '111'],
  V: ['101', '101', '101', '101', '010'], W: ['101', '101', '111', '111', '101'], X: ['101', '101', '010', '101', '101'],
  Y: ['101', '101', '010', '010', '010'], Z: ['111', '001', '010', '100', '111'],
  'Ç': ['011', '100', '100', '100', '011', '010'], 'Í': ['001', '000', '111', '010', '111'], 'Ö': ['101', '000', '111', '101', '111'],
  0: ['111', '101', '101', '101', '111'], 1: ['010', '110', '010', '010', '111'], 2: ['111', '001', '111', '100', '111'],
  3: ['111', '001', '011', '001', '111'], 4: ['101', '101', '111', '001', '001'], 5: ['111', '100', '111', '001', '111'],
  6: ['111', '100', '111', '101', '111'], 7: ['111', '001', '010', '010', '010'], 8: ['111', '101', '111', '101', '111'],
  9: ['111', '101', '111', '001', '111'],
  '-': ['000', '000', '111', '000', '000'], '.': ['000', '000', '000', '000', '010'], ':': ['000', '010', '000', '010', '000'],
  '!': ['010', '010', '010', '000', '010'], "'": ['010', '010', '000', '000', '000'], '&': ['010', '101', '010', '101', '011'],
  '/': ['001', '001', '010', '100', '100'], '?': ['111', '001', '010', '000', '010'], ' ': ['000', '000', '000', '000', '000'],
  '%': ['101', '001', '010', '100', '101'], '·': ['000', '000', '010', '000', '000'],
  '↗': ['000', '111', '011', '101', '000'],   // an outbound link, like the site's "↗"
};
export const textWidth = (s, gap = 1) => s.length ? s.length * (3 + gap) - gap : 0;
/** draw uppercase text with FONT3; returns the width in pixels */
export function text(c, s, x, y, col, gap = 1) {
  let cx = x;
  for (const ch of s.toUpperCase()) { const g = FONT3[ch]; if (g) glyph(c, g, cx, y, col); cx += 3 + gap; }
  return cx - x - gap;
}

/* ================= Clock hands ================= */
// Clocks show the city's time: the website, OBS and PNG saves call render(t, { clock }) with the city's local time
// in minutes after midnight (site/js/app.js). Left out (loop videos, checks) the hands rest at `rest` (610 = 10:10),
// so render stays a pure function of t and its options. Bake the face; draw the hands per frame on top of it.
/** hour hand hLen px and minute hand mLen px from (cx, cy), and the pin (null: none); mCol and pin default to hCol */
export function hands(c, cx, cy, clock, rest, hLen, mLen, hCol, mCol = hCol, pin = hCol) {
  const m = clock ?? rest;
  for (let j = 0; j < 2; j++) {
    const a = j ? TAU * (m % 720) / 720 : TAU * (m % 60) / 60, len = j ? hLen : mLen, s = Math.sin(a), co = Math.cos(a);
    c.fillStyle = j ? hCol : mCol;
    for (let k = 1; k <= len; k++) c.fillRect(Math.round(cx + s * k), Math.round(cy - co * k), 1, 1);
  }
  if (pin) { c.fillStyle = pin; c.fillRect(cx, cy, 1, 1); }
}

/* ================= Billboard ================= */
// Every city has one billboard. A scene exports its face as `billboard: { x, y, w, h }`, paints the city's own
// made-up ad on it, and takes `render(t, { pitch, billboard })`. `pitch` names a face painted over the ad:
// - true: the pitch, which invites businesses to put their ad there, for promo captures only (CDP_PITCH=1 snaps, the
//   billboard promo video);
// - 'job': the city's own ad takes turns with "new cities on X" (the site's account) and "post a job for free" (jobs/post/),
//   on the website (site/js/app.js);
// - 'x': the same without "post a job" (the admin page's switch), the city's own ad in its place.
// pitchOnShow says which one is on show, for the website's click.
// OBS, PNG saves and social images call render(t) and keep the city's own ad (on the website site/js/app.js lays a
// clickable spot over the face); the loop videos pass billboard: false and leave it out.
// A sign belongs to its scene: on a building at that building's scale, and lit like the night around it. gradeAd
// dims, tints and weathers a prerendered own ad for that (below).
export const PITCH_MIN_W = 41, PITCH_MIN_H = 14;       // two lines of up to 10 FONT3 characters, 1 px margin
// Each face is a list of pages: two lines, the second in the page's accent (the city's own unless it has one), and
// what the website's billboard opens while the page shows (`link`). A null page shows the city's own ad: the pages
// around it roll in and out over it.
const PITCH_PAGES = [['YOUR AD', 'HERE'], ['YOUR SHOP', 'IN PIXELS'], ['AD SPACE', 'AVAILABLE']].map(lines => ({ lines, link: 'pitch' }));
const JOB_PAGES = [['POST A JOB', 'FOR FREE'], ['HIRING?', 'TELL US']].map(lines => ({ lines, link: 'job' }));
const X_PAGE = { lines: ['NEW CITIES', 'ON X ↗'], link: 'x' };   // X has no brand colour: the city's accent
const PITCH_FACES = {
  pitch: PITCH_PAGES,
  job: [null, null, null, X_PAGE, ...JOB_PAGES],                   // 12 s the city's own ad, 4 s "new cities on X", 8 s "post a job"
  x: [null, null, null, X_PAGE, null, null],                       // as 'job', the own ad in "post a job"'s 8 s: X keeps its turn
};
const PITCH_STEPS = 60;                                // page changes per loop (every 4 s); a multiple of every page count
/**
 * Pre-render the faces for a w x h billboard (build time, in create()). At loop time t, for a `pitch` option:
 * - covers(t, pitch): whether the face hides the whole billboard, so the scene can skip its own ad;
 * - draw(c, t, x, y, pitch): paints the face (opaque where it shows): two lines, the second in `accent` (the city's
 *   own, unless the page has one), rolling up to the next page every 4 s. No pitch paints nothing, nor a face this
 *   engine doesn't know (a tab left open across a deploy may ask for one that is gone).
 * A scene draws its own ad unless the face covers it, then the face: the ad shows through the face's null pages.
 */
export function makePitch(w, h, { accent, ink = '#f3ead6', bg = '#0d0f24', lit = '#1c2046' }) {
  const y0 = Math.round((h - 12) / 2);
  const back = ditherFill(w, h, [lit, bg], (i, j) => j / 4);   // lit along the top edge, flat behind the text
  const faces = {};
  for (const [name, pages] of Object.entries(PITCH_FACES)) faces[name] = pages.map(page => {
    if (!page) return null;
    const [c, x] = makeCanvas(w, h);
    x.drawImage(back, 0, 0);
    page.lines.forEach((s, k) => text(x, s, Math.round((w - textWidth(s)) / 2), y0 + k * 7, k ? page.accent ?? accent : ink));
    return c;
  });
  const at = (t, pitch) => {                            // the page on show, the next one and how far it has rolled in
    const pages = pitch && faces[pitch === true ? 'pitch' : pitch], i = step(t, PITCH_STEPS);
    if (!pages) return null;
    const s = Math.round(smooth(0.93, 1, frac(t * PITCH_STEPS / LOOP)) * h);   // the roll takes the last ~0.3 s
    return { a: pages[i % pages.length], b: pages[(i + 1) % pages.length], s };
  };
  return {
    covers(t, pitch) {
      const on = at(t, pitch);
      if (!on) return false;
      const { a, b, s } = on;
      return (!!a || s >= h) && (!!b || s <= 0);
    },
    draw(c, t, x, y, pitch) {
      const on = at(t, pitch);
      if (!on) return;
      const { a, b, s } = on;
      if (a && s < h) c.drawImage(a, 0, s, w, h - s, x, y, w, h - s);
      if (b && s > 0) c.drawImage(b, 0, 0, w, s, x, y + h - s, w, s);
    },
  };
}
/** What a `pitch` face shows at loop time t: 'x', 'job', 'pitch', or null for the city's own ad (no face, or one of
 * its null pages); a page counts from when it has rolled in. The website's billboard clicks through to it (site/js/app.js). */
export function pitchOnShow(t, pitch) {
  const pages = pitch && PITCH_FACES[pitch === true ? 'pitch' : pitch];
  return pages?.[step(t, PITCH_STEPS) % pages.length]?.link ?? null;
}
/**
 * Night-grade a city's own ad (build time), so the sign is lit like its scene and stays dimmer than the landmark (the
 * review guide: nothing near the landmark outshines it). On w x h RGBA bytes, in place: `level` scales the light
 * (1 keeps it), `mix` pulls every colour towards `tint`, the scene's night colour, and `wear` (0..1) weathers a
 * painted or pasted face with run-off streaks under the top edge and a few scuffed patches, the same for a `seed`
 * every time. Clear pixels stay clear. Returns the bytes.
 */
export function gradePixels(d, w, h, { level = 1, tint = '#0d0f24', mix = 0, wear = 0, seed = 1 } = {}) {
  const T = hex(tint);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const o = (y * w + x) * 4;
    if (!d[o + 3]) continue;
    let m = mix;
    if (wear) {
      const run = hash(seed, x) < 0.22 ? hash(seed, x, 1) * Math.max(0, 1 - y / (h * (0.4 + 0.6 * hash(seed, x, 2)))) : 0;
      const scuff = hash(seed, x >> 2, 100 + (y >> 1)) < 0.12 ? 1 : 0;
      m = Math.min(1, m + wear * (0.5 * run + 0.25 * scuff));
    }
    for (let k = 0; k < 3; k++) d[o + k] = Math.round(lerp(d[o + k] * level, T[k], m));
  }
  return d;
}
/** gradePixels on a prerendered ad canvas, in place (see there); returns the canvas */
export function gradeAd(c, opts) {
  const x = c.getContext('2d'), img = x.getImageData(0, 0, c.width, c.height);
  gradePixels(img.data, c.width, c.height, opts);
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Water / wet-surface helpers ================= */
/**
 * Rippled mirror of the rows above `top` into rows [top, top + rows).
 * Call it after everything above the waterline is drawn. Reads ctx.canvas.
 * k1/k2 are integer ripple cycles per loop (loop-safe).
 */
let reflectScratch = null;
export function reflect(ctx, t, { top, rows, squash = 1.6, alpha = 0.5, k1 = 45, k2 = 110, amp = 0.6, grow = 0.05, x = 0, w = W }) {
  // Snapshot the mirrored band once. Drawing a canvas onto itself row by row makes the
  // browser copy the whole bitmap for every row (~1.7 ms/frame on a CPU canvas).
  const span = Math.min(top, Math.floor((rows - 1) * squash) + 1);
  if (span <= 0) return;
  const y0 = top - span;
  if (!reflectScratch) reflectScratch = makeCanvas(W, H);
  const [scratch, sx] = reflectScratch;
  sx.globalCompositeOperation = 'copy';
  sx.drawImage(ctx.canvas, x, y0, w, span, x, 0, w, span);
  for (let k = 0; k < rows; k++) {
    const sy = top - 1 - Math.floor(k * squash);
    if (sy < 0) break;
    const off = Math.round(Math.sin(TAU * k1 * t / LOOP + k * 0.55) * (amp + k * grow) + Math.sin(TAU * k2 * t / LOOP - k * 1.3) * 0.7);
    ctx.globalAlpha = alpha * (1 - k / (rows + 20));
    ctx.drawImage(scratch, x, sy - y0, w, 1, x + off, top + k, w, 1);
  }
  ctx.globalAlpha = 1;
}
/** broken, shimmering vertical light streak on water under a light at x (loop-safe) */
export function streak(ctx, t, sx, w, col, a, seed, y0, y1) {
  ctx.fillStyle = col;
  for (let y = y0; y < y1; y++) {
    const k = y - y0;
    const dsh = Math.sin(k * 1.9 + seed + TAU * 150 * t / LOOP) + 0.7 * Math.sin(k * 0.63 - TAU * 97 * t / LOOP + seed * 2);
    if (dsh < 0.15) continue;
    const len = Math.max(1, Math.round(w * (0.55 + 0.45 * Math.sin(k * 0.47 + TAU * 131 * t / LOOP + seed)) + k * 0.06 * w));
    const xo = Math.round(Math.sin(TAU * 83 * t / LOOP + k * 0.83 + seed) * (1 + k * 0.04));
    ctx.globalAlpha = a * Math.max(0, 1 - k / 52) * (0.6 + 0.4 * Math.min(1, dsh));
    ctx.fillRect(Math.round(sx - len / 2) + xo, y, len, 1);
  }
  ctx.globalAlpha = 1;
}
